import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { Reveal } from "@/components/Reveal";
import { DirectionsCarousel } from "@/components/DirectionsCarousel";
import { submitContatti } from "@/lib/contatti.functions";
import { ObfuscatedEmail } from "@/components/ObfuscatedEmail";
import { MappaGoogle } from "@/components/MappaGoogle";

export const Route = createFileRoute("/contatti")({
  head: () => ({
    meta: [
      { title: "Contatti — La Bottega delle Carni da Marco" },
      { name: "description", content: "Vieni a trovarci in Via J.F. Kennedy 1 a Campobasso. Telefono, WhatsApp, email e orari di apertura della bottega." },
      { property: "og:title", content: "Contatti — La Bottega delle Carni da Marco" },
      { property: "og:description", content: "Indirizzo, telefono e orari della Bottega delle Carni da Marco a Campobasso." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/contatti" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/contatti" }],
  }),
  component: ContattiPage,
});

function ContattiPage() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const send = useServerFn(submitContatti);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    setSending(true);
    try {
      const res = await send({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          message: String(fd.get("message") ?? ""),
        },
      });
      // Il messaggio non viene salvato: arriva solo su Telegram. Se l'invio è fallito
      // non mostriamo "inviato", altrimenti la richiesta andrebbe persa in silenzio.
      if (!res.telegramSent) {
        toast.error("Il messaggio non è stato recapitato. Riprova oppure chiamaci al 340 748 7310.");
        return;
      }
      form.reset();
      setSent(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Errore invio messaggio");
    } finally {
      setSending(false);
    }
  };

  const ConfirmScreen = (
    <div className="text-center py-10 px-4">
      <div className="w-16 h-16 rounded-full bg-brand-green/15 text-brand-green flex items-center justify-center mx-auto">
        <CheckCircle2 size={40} strokeWidth={1.5} />
      </div>
      <h3 className="font-display text-brand-charcoal text-2xl md:text-3xl mt-5">Messaggio inviato</h3>
      <p className="font-italic-serif italic text-brand-warm-gray mt-3 max-w-md mx-auto">
        Grazie per averci scritto. Ti risponderemo al più presto direttamente al recapito che ci hai lasciato.
      </p>
      <button
        onClick={() => setSent(false)}
        className="mt-6 inline-flex items-center gap-2 text-brand-red hover:text-brand-red-hover text-sm uppercase tracking-[0.16em]"
      >
        Invia un altro messaggio
      </button>
    </div>
  );

  return (
    <>
      {/* HEADER */}
      <section className="bg-brand-cream pt-16 pb-12 border-b border-brand-gold/20">
        <div className="container mx-auto px-4 text-center">
          <Eyebrow>Vieni a trovarci</Eyebrow>
          <h1 className="font-display text-brand-charcoal text-4xl md:text-6xl mt-3">Contatti</h1>
          <p className="text-brand-warm-gray mt-4 max-w-2xl mx-auto font-italic-serif italic text-lg">
            Nel cuore di Campobasso, in Via J. F. Kennedy 1 — dentro il Tigre, ma indipendenti come una bottega di una volta.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 grid lg:grid-cols-5 gap-10">
          {/* INFO */}
          <Reveal className="lg:col-span-2 space-y-6">
            <div>
              <SectionTitle>Dove siamo</SectionTitle>
              <ul className="mt-6 space-y-5 text-brand-charcoal">
                <li className="flex gap-4">
                  <span className="w-11 h-11 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center shrink-0"><MapPin size={18} /></span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Indirizzo</div>
                    <div className="mt-1">Via J. F. Kennedy, 1<br />86100 Campobasso (CB)</div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <a href="tel:+393407487310" className="flex gap-4 items-start group">
                    <span className="w-11 h-11 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center shrink-0 group-hover:bg-brand-red group-hover:text-white transition-colors"><Phone size={18} /></span>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Telefono</div>
                      <div className="mt-1 group-hover:text-brand-red transition-colors">+39 340 748 7310</div>
                    </div>
                  </a>
                </li>
                <li className="flex gap-4">
                  <a href="https://wa.me/393407487310" className="flex gap-4 items-start group">
                    <span className="w-11 h-11 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 group-hover:bg-brand-green group-hover:text-white transition-colors"><MessageCircle size={18} /></span>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">WhatsApp</div>
                      <div className="mt-1 group-hover:text-brand-green transition-colors">+39 340 748 7310</div>
                    </div>
                  </a>
                </li>
                 <li className="flex gap-4">
                   <ObfuscatedEmail className="flex gap-4 items-start group">
                     {(email) => (
                       <>
                         <span className="w-11 h-11 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center shrink-0 group-hover:bg-brand-red group-hover:text-white transition-colors"><Mail size={18} /></span>
                         <div>
                           <div className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Email</div>
                           <div className="mt-1 group-hover:text-brand-red transition-colors">{email}</div>
                         </div>
                       </>
                     )}
                   </ObfuscatedEmail>
                 </li>
                <li className="flex gap-4">
                  <span className="w-11 h-11 rounded-full bg-brand-gold/15 text-brand-charcoal flex items-center justify-center shrink-0"><Clock size={18} /></span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Orari</div>
                    <div className="mt-1 leading-relaxed">
                      Lun–Sab&nbsp;&nbsp;8:00 – 13:30 / 16:00 – 20:00<br />
                      Domenica chiuso <span className="text-brand-warm-gray italic">(ritiro su prenotazione)</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>

          {/* FORM */}
          <Reveal delay={0.1} className="lg:col-span-3 order-last lg:order-none hidden lg:block">
            <div className="bg-white rounded-md p-6 md:p-8 shadow-[var(--shadow-soft)]">
              {sent ? ConfirmScreen : (<>
              <SectionTitle>Scrivici</SectionTitle>
              <p className="font-italic-serif italic text-brand-warm-gray mt-2">
                Domande, ordini speciali o curiosità: rispondiamo entro 24 ore.
              </p>
              <form onSubmit={onSubmit} className="mt-6 grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Nome</span>
                  <input required name="name" type="text" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Telefono</span>
                  <input name="phone" type="tel" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Email</span>
                  <input required name="email" type="email" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Messaggio</span>
                  <textarea required name="message" rows={5} className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red resize-none" />
                </label>
                <p className="sm:col-span-2 text-xs text-brand-warm-gray leading-relaxed">
                  Inviando il messaggio dichiari di aver letto l'<Link to="/privacy" className="text-brand-red underline underline-offset-2">informativa privacy</Link>. Useremo i tuoi dati solo per risponderti.
                </p>
                <div className="sm:col-span-2 flex justify-end">
                  <button disabled={sending} type="submit" className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] disabled:opacity-60">
                    <Send size={14} /> {sending ? "Invio..." : "Invia messaggio"}
                  </button>
                </div>
              </form>
              </>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAPPA */}
      <section className="pb-20">
        <div className="container mx-auto px-4 mb-16">
          <DirectionsCarousel />
        </div>
        <div className="container mx-auto px-4">
          <MappaGoogle />
        </div>
        {/* FORM (mobile only — appare dopo la mappa) */}
        <div className="container mx-auto px-4 mt-12 lg:hidden">
          <div className="bg-white rounded-md p-6 md:p-8 shadow-[var(--shadow-soft)]">
            {sent ? ConfirmScreen : (<>
            <SectionTitle>Scrivici</SectionTitle>
            <p className="font-italic-serif italic text-brand-warm-gray mt-2">
              Domande, ordini speciali o curiosità: rispondiamo entro 24 ore.
            </p>
            <form onSubmit={onSubmit} className="mt-6 grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Nome</span>
                <input required name="name" type="text" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Telefono</span>
                <input name="phone" type="tel" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Email</span>
                <input required name="email" type="email" className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.18em] text-brand-warm-gray">Messaggio</span>
                <textarea required name="message" rows={5} className="mt-1 w-full px-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red resize-none" />
              </label>
              <p className="sm:col-span-2 text-xs text-brand-warm-gray leading-relaxed">
                Inviando il messaggio dichiari di aver letto l'<Link to="/privacy" className="text-brand-red underline underline-offset-2">informativa privacy</Link>. Useremo i tuoi dati solo per risponderti.
              </p>
              <div className="sm:col-span-2 flex justify-end">
                <button disabled={sending} type="submit" className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] disabled:opacity-60">
                  <Send size={14} /> {sending ? "Invio..." : "Invia messaggio"}
                </button>
              </div>
            </form>
            </>)}
          </div>
        </div>
      </section>
    </>
  );
}
